import { useSyncExternalStore } from "react";

/**
 * Global light/dark theme control.
 *
 * The theme is expressed as `data-theme="dark" | "light"` on <html>. All colour
 * tokens live in index.css (base @theme = Light·Gold, [data-theme="dark"] block
 * = Dark·Gold), so flipping this attribute re-skins the entire app — no
 * component needs its own light/dark styles.
 *
 * The initial attribute is set by an inline script in index.html (before first
 * paint, to avoid a flash), reading the same localStorage key used here.
 */
export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "bl-theme";

const listeners = new Set<() => void>();

function readAttr(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

export function getTheme(): Theme {
  return readAttr();
}

/** The user's explicit choice, or null when they've never used the toggle. */
export function getStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Apply the signed-in default (Light·Gold) for a user who has never picked a
 * theme — the fallback for a session restored from an existing token, where no
 * sign-in happens. A fresh sign-in is stronger: AuthContext.storeToken forces
 * light outright, so every login lands in light regardless of the last session.
 *
 * The signed-out surfaces — landing page and auth screens — stay Dark·Gold: that
 * is the brand's front door, and they pin it with a scoped `data-theme` anyway.
 *
 * Deliberately does NOT write to localStorage: an untouched preference stays
 * untouched, so changing this default later won't be blocked by a value the user
 * never chose.
 */
export function applySignedInDefault(): void {
  if (typeof document === "undefined") return;
  if (getStoredTheme()) return; // an explicit choice always wins
  if (document.documentElement.getAttribute("data-theme") === "light") return;
  document.documentElement.setAttribute("data-theme", "light");
  listeners.forEach((l) => l()); // keep useTheme() consumers (maps, Stripe) in sync
}

export function setTheme(theme: Theme): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", theme);
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* private mode / storage disabled — theme still applies for the session */
  }
  listeners.forEach((l) => l());
}

export function toggleTheme(): void {
  setTheme(getTheme() === "dark" ? "light" : "dark");
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Reactive current theme; re-renders the caller whenever the theme changes. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, () => "dark");
}

/**
 * Stripe Elements `appearance` config matching the active theme — gold accent
 * on both, dark "night" surfaces for Dark·Gold and clean light surfaces for
 * Light·Gold. Use everywhere a Payment Element mounts so the form never renders
 * as a dark island inside a light modal (or vice versa).
 */
export function stripeAppearance(theme: Theme = getTheme()) {
  const shared = {
    fontFamily: "system-ui, sans-serif",
    borderRadius: "10px",
    spacingUnit: "4px",
  };
  return theme === "light"
    ? {
        theme: "stripe" as const,
        variables: {
          ...shared,
          colorPrimary: "#a97b1d",
          colorBackground: "#ffffff",
          colorText: "#231b0c",
          colorTextSecondary: "#6b6045",
          colorTextPlaceholder: "#a49a82",
          colorDanger: "#d84a3d",
        },
      }
    : {
        theme: "night" as const,
        variables: {
          ...shared,
          colorPrimary: "#D4A93E",
          colorBackground: "#201a12",
          colorText: "#f3edde",
          colorTextSecondary: "#a99f88",
          colorTextPlaceholder: "#8a8069",
          colorDanger: "#f87171",
        },
      };
}
